<template>
  <div
    class="pane-form"
    :class="{ 'is-editing': isEditing }"
  >
    <VisualTextEditor
      v-model:value="newPrompt"
      data-locator="add-prompt-textarea"
      :clean-content-on-blur="false"
      :error-messages="newPromptErrors"
      hide-menubar
      :max-length="NEW_PROMPT_MAX_LENGTH"
      :placeholder="placeholder"
      clean-content-on-validation
      keep-toolbar-on-blur
      counter="footer"
      :counter-label="$t('common.characters')"
      :disabled="disabled"
      ref="visualTextEditor"
      cleanPasteStyle
      @blur="onBlur"
      @focus="onFocus"
    />

    <div class="pane-form__footer d-flex align-center px-2 py-2">
      <div class="pane-form__footer-left d-flex align-center">
        <AppTooltip
          :disabled="disabled"
          location="top"
          data-locator="ai-ask-doc-open-documents-picker-tooltip"
        >
          <template #activator="{ props }">
            <app-button
              v-if="isAskDoc"
              v-bind="props"
              data-locator="ai-ask-doc-open-documents-picker"
              type="icon"
              size="small"
              variant="text"
              class="pane-form__plus-button"
              :disabled="disabled"
              @click="onOpenDocumentsPicker"
            >
              <app-material-icon
                type="fa"
                name="plus"
                color="#375576"
                size="xs"
              />
            </app-button>
          </template>

          {{ $t('common.ai-assistant.AIAssistantPaneForm.selectDocuments') }}
        </AppTooltip>

        <div
          v-if="selectedDocuments.length > 0"
          class="pane-form__sources"
          data-locator="ai-ask-doc-selected-documents"
        >
          <v-menu
            v-model="isSourcesMenuOpen"
            :close-on-content-click="false"
            location="top start"
            offset="8"
            content-class="pane-form__sources-menu"
          >
            <template #activator="{ props: menuProps }">
              <AppTooltip
                location="top"
                data-locator="ai-ask-doc-selected-documents-tooltip"
              >
                <template #activator="{ props: tooltipProps }">
                  <button
                    v-bind="{ ...menuProps, ...tooltipProps }"
                    type="button"
                    class="pane-form__sources-pill rounded-pill"
                    data-locator="ai-ask-doc-selected-documents-pill"
                  >
                    <div class="d-flex align-center ga-2 min-w-0">
                      <app-material-icon
                        name="file-lines"
                        type="fa"
                        size="xs"
                        color="#005E9E"
                      />
                      <span class="pane-form__sources-label">
                        {{ $t('common.ai-assistant.AIAssistantPaneForm.multipleSources', { count: selectedDocuments.length }) }}
                      </span>
                    </div>
                  </button>
                </template>

                {{ $t('common.ai-assistant.AIAssistantPaneForm.multipleSourcesTooltip') }}
              </AppTooltip>
            </template>

            <div class="pane-form__sources-menu-content">
              <div
                v-for="doc in selectedDocuments"
                :key="`${doc.type}-${doc.id}`"
                class="pane-form__source-item"
                :data-locator="`ai-ask-doc-source-item-${doc.id}`"
              >
                <div class="pane-form__source-item-left">
                  <ClosdFildersIcon
                    :document="doc"
                    :size="18"
                    class="pane-form__source-item-file-icon"
                  />

                  <AppTooltip location="top">
                    <template #activator="{ props: tooltipProps }">
                      <span
                        v-bind="tooltipProps"
                        class="pane-form__source-item-name"
                      >
                        {{ doc.name || doc.basename }}
                      </span>
                    </template>

                    {{ doc.name || doc.basename }}
                  </AppTooltip>
                </div>

                <AppTooltip
                  location="top"
                  :data-locator="`ai-ask-doc-source-remove-tooltip-${doc.id}`"
                >
                  <template #activator="{ props: tooltipProps }">
                    <app-button
                      v-bind="tooltipProps"
                      type="icon"
                      size="x-small"
                      variant="text"
                      class="pane-form__source-item-remove"
                      :data-locator="`ai-ask-doc-source-remove-${doc.id}`"
                      @click.stop="removeSelectedDocument(doc)"
                    >
                      <app-material-icon
                        name="xmark"
                        color="#375576"
                        type="fa"
                        size="xxs"
                      />
                    </app-button>
                  </template>

                  {{ $t('common.ai-assistant.AIAssistantPaneForm.removeDocument') }}
                </AppTooltip>
              </div>
            </div>
          </v-menu>
        </div>
      </div>

      <div class="ml-auto d-flex align-center">
         <v-divider class="my-auto mx-1" vertical style="height: 20px;" />

        <app-button
          data-locator="post-prompt-button"
          :disabled="!canPostPrompt"
          :loading="disabled"
          class="post-prompt-button"
          size="small"
          type="icon"
          @click="onPostPrompt"
        >
          <app-icon-ds icon="arrow-up" size="xs" color="currentColor" />
        </app-button>
      </div>
    </div>

    <SharedDocumentsPickerDialog
      v-if="isAskDoc"
      v-model="localSelectedDocuments"
      :is-open="isDocumentsDialogOpen"
      :allowed-types="['file']"
      @update:isOpen="isDocumentsDialogOpen = $event"
      @confirm="onConfirmDocuments"
    />
  </div>
</template>

<script>
import SharedDocumentsPickerDialog from '@/common/documents-picker/SharedDocumentsPickerDialog.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { escapeHtml } from '@/common/utils/strings'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { AIAssistantQuestionTypes } from '@/types/ai-assistant'

import { canPostPrompt, NEW_PROMPT_MAX_LENGTH } from './aiAssistantPaneFormHelpers'
import AppTooltip from '../AppTooltip.vue'
const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPaneForm'

export default {
  name: 'AIAssistantPaneForm',
  components: {
    VisualTextEditor,
    SharedDocumentsPickerDialog,
    ClosdFildersIcon,
    AppTooltip,
  },
  emits: ['data-unsaved', 'post-prompt', 'update:selectedDocuments'],
  props: {
    cleared: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    questionType: {
      type: String,
      required: true,
    },
    selectedDocuments: {
      type: Array,
      default: () => ([]),
    },
  },
  data: () => ({
    NEW_PROMPT_MAX_LENGTH,
    TRANSLATION_KEY_PREFIX,
    isEditing: false,
    newPrompt: '',
    isDocumentsDialogOpen: false,
    isSourcesMenuOpen: false,
    localSelectedDocuments: [],
  }),
  computed: {
    isReadonly () {
      return this.disabled
    },
    isAskDoc () {
      return this.questionType === AIAssistantQuestionTypes.ASK_DOC
    },
    canPostPrompt () {
      return canPostPrompt({ disabled: this.disabled, prompt: this.newPrompt })
    },
    newPromptLength () {
      return escapeHtml(this.newPrompt).length
    },
    newPromptIsTooLong () {
      return this.newPromptLength > this.NEW_PROMPT_MAX_LENGTH
    },
    newPromptErrors () {
      const errors = []

      if (this.newPromptIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    placeholder () {
      if (this.isReadonly) {
        return ''
      }
      if (this.questionType === AIAssistantQuestionTypes.ASK_DOC) {
        return this.$t(`${TRANSLATION_KEY_PREFIX}.newPromptPlaceholderAskDoc`)
      }
      return this.$t(`${TRANSLATION_KEY_PREFIX}.newPromptPlaceholder`)
    },
  },
  watch: {
    cleared (value) {
      if (value) {
        this.onClear()
      }
    },
    selectedDocuments: {
      immediate: true,
      deep: true,
      handler (value) {
        this.localSelectedDocuments = Array.isArray(value) ? [...value] : []

        if (!this.localSelectedDocuments.length) {
          this.isSourcesMenuOpen = false
        }
      },
    },
  },
  methods: {
    onClear () {
      this.$refs.visualTextEditor.clear()
    },
    onBlur () {
      this.toggleEditMode(false)
    },
    onFocus () {
      this.toggleEditMode(true)
    },
    onPostPrompt () {
      if (!this.canPostPrompt) return
      this.$emit('post-prompt', this.newPrompt)
    },
    toggleEditMode (isEditing) {
      if (this.isReadonly) return
      this.isEditing = isEditing
    },
    onOpenDocumentsPicker () {
      if (this.disabled) return
      this.isDocumentsDialogOpen = true
    },
    onConfirmDocuments (documents) {
      const sanitized = (documents || []).filter(doc => doc?.type === 'file')
      this.localSelectedDocuments = sanitized
      this.$emit('update:selectedDocuments', sanitized)
    },
    removeSelectedDocument (doc) {
      this.localSelectedDocuments = this.selectedDocuments.filter(
        item => !(item?.id === doc?.id && item?.type === doc?.type),
      )

      this.$emit('update:selectedDocuments', this.localSelectedDocuments)

      if (!this.localSelectedDocuments.length) {
        this.isSourcesMenuOpen = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pane-form {
  outline: 1px solid rgb(var(--v-theme-grey-lighten-4));
  background-color: white;
  border-radius: 8px;
  overflow: hidden;

  &.is-editing {
    outline: 2px solid rgb(var(--v-theme-info-darken-1));
  }

  &.is-read-only {
    outline: 1px solid rgb(var(--v-theme-grey-200));
  }

  :deep(.editor__content .ProseMirror) {
    border: none !important;
    max-height: 100px;
  }
  :deep(.editor__menubar) {
    border: none !important;
    padding: 4px 0;
  }

  :deep(.editor__content .ProseMirror p.is-editor-empty:first-child::before) {
    color: #5E7997;
  }

  :deep(.text-counter) {
    bottom: 40px;
    right: 70px;
    position: absolute;
    color: #2A476A;
  }

  &:not(.is-editing) :deep(.text-counter) {
    opacity: 0;
  }

  :deep(.editor__errors) {
    display: none;
  }
}

.pane-form__footer {
  min-height: 44px;
  gap: 8px;
}

.pane-form__footer-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.pane-form__plus-button {
  border-radius: 100%;
  color: #375576 !important;
}

.pane-form__sources {
  display: flex;
  align-items: center;
  min-width: 0;
}

.pane-form__sources-pill {
  height: 32px;
  padding: 0 10px;
  background: #DAE9FC;
  color: #005E9E;
}

.pane-form__sources-label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

:deep(.pane-form__sources-menu) {
  box-shadow: 0 8px 24px rgba(0, 23, 46, 0.16);
  border: 1px solid #DFE3E8;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.pane-form__sources-menu-content {
  min-width: 260px;
  max-width: 320px;
  padding: 8px;
}

.pane-form__source-item {
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 6px;
  padding: 4px 6px;
  transition: background 0.25s ease;
}

.pane-form__source-item:hover {
  background: #00172E0D;
}

.pane-form__source-item-left {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pane-form__source-item-file-icon {
  flex: 0 0 auto;
}

.pane-form__source-item-name {
  min-width: 0;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #2A476A;
  font-size: 13px;
  cursor: default;
}

.pane-form__source-item-remove {
  min-width: 20px !important;
  width: 20px;
  height: 20px;
  color: #EDF0F2 !important;
  flex: 0 0 auto;
  border-radius: 50%;
  background-color: #EDF0F2 !important;
}

.post-prompt-button {
  border-radius: 100%;

  &:disabled {
    background-color: #EDF0F2;
    color: #5E7997;
  }
  &:not(:disabled), &:focus {    
    background-color: rgb(var(--v-theme-purple-darken-1));
    color: white !important;

    &:hover, &:active {
      background-color: rgb(var(--v-theme-purple-darken-4));
    }
  }
}
</style>
